import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import debounce from 'lodash.debounce'
import { Button, Switch, Tooltip } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useGetOrgCredentialTypes } from '@/service-library/hooks/org-credential-types'
import { useGetProjectTemplates } from '@/service-library/hooks/project-templates'
import {
  useDeleteProjectWithDefaults,
  useUpdateProject,
} from '@/service-library/hooks/projects'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { ProjectCategory } from '@/types/projects'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import ListAutocomplete from '@/components/list-autocomplete/ListAutocomplete'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import ProjectCategoryAutocomplete from '@/components/projects/ProjectCategoryAutocomplete'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import SettingsRow from '@/components/shared/settings/SettingsRow'
import SettingsSection from '@/components/shared/settings/SettingsSection'
import SuperUserOnlyIcon from '@/components/shared/super-user/SuperUserOnlyIcon'
import DeleteProjectDialog from './DeleteProjectDialog'
import ProjectDescriptionField from './ProjectDescriptionField'
import ProjectNameField from './ProjectNameField'

export default function ProjectSettings() {
  const { project, queryKey } = useProjectContext()
  const isSuperUser = useIsSuperUser()

  const { orgCredentialTypes } = useGetOrgCredentialTypes({
    enabled: isSuperUser,
  })

  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1',
      project_id: project.id,
    },
  })

  const deleteOverlay = useOverlay()

  const navigate = useNavigate()

  const { updateProject } = useUpdateProject({
    detailQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Unable to update project. Please try again later.')
    },
  })

  const { deleteProject } = useDeleteProjectWithDefaults({
    sideEffectQueryKeys: [queryKeys.projects.lists()],
    onSuccess: () => {
      showSuccessSnackbar('Project Deleted')
      navigate('/projects')
    },
    onError: () => {
      showErrorSnackbar('Unable to delete project. Please try again later.')
    },
  })

  const onDeleteProject = () => {
    deleteOverlay.close()
    deleteProject(project.id)
  }

  const debouncedUpdateProject = useMemo(
    () => debounce(updateProject, 250),
    [updateProject],
  )

  const [internalCategory, setInternalCategory] = useState(
    project.project_category as ProjectCategory,
  )

  function handleRemoveDupEntities(checked: boolean) {
    debouncedUpdateProject({ ...project, remove_duplicate_entities: checked })
  }

  function handleExcludeFromDocRouting(checked: boolean) {
    debouncedUpdateProject({ ...project, exclude_from_doc_routing: checked })
  }

  function handleCategoryChange(category: ProjectCategory) {
    setInternalCategory(category)
    updateProject(
      {
        ...project,
        project_category_id: category.id,
      },
      {
        onError: () =>
          setInternalCategory(project.project_category as ProjectCategory),
      },
    )
  }

  const isTemplateProject = projectTemplates.length > 0

  return (
    <>
      <ProjectBreadcrumb label="Project General" url="../general" />

      <SettingsPage
        title="General"
        description="The name, category and document handling for this project."
      >
        <SettingsSection title="Project details">
          <SettingsRow
            label="Name"
            description="Shown on the Projects page and in the breadcrumb."
            htmlFor="project-name"
          >
            <ProjectNameField
              id="project-name"
              project={project}
              updateProject={updateProject}
            />
          </SettingsRow>
          <SettingsRow
            label="Category"
            description="Groups this project with others on the Projects page."
            htmlFor="project-category"
          >
            <ProjectCategoryAutocomplete
              id="project-category"
              label=""
              variant="outlined"
              selected={internalCategory}
              onCategoryChange={handleCategoryChange}
              sx={{ width: 280 }}
            />
          </SettingsRow>
          <SettingsRow
            label="Document Description"
            description="Describe the type of documents this project processes. This helps PixyAI know what kind of documents to sort into this project."
            layout="stacked"
            htmlFor="project-description"
          >
            <ProjectDescriptionField
              id="project-description"
              project={project}
              updateProject={updateProject}
            />
          </SettingsRow>
          {isSuperUser && (
            <SettingsRow
              label={
                <>
                  Org credential type <SuperUserOnlyIcon />
                </>
              }
              description="The type of credential this project expects for the data it retrieves."
              htmlFor="org-credential-type"
            >
              <ListAutocomplete
                id="org-credential-type"
                variant="outlined"
                inputSize="small"
                autoSelect={false}
                options={orgCredentialTypes}
                selected={
                  orgCredentialTypes.find(
                    (t) => t.id === project.org_credential_type_id,
                  ) ?? null
                }
                setSelected={(value) =>
                  updateProject({
                    ...project,
                    org_credential_type_id: value.id,
                  })
                }
                onChange={(_e, value) =>
                  updateProject({
                    ...project,
                    org_credential_type_id: value?.id ?? null,
                  })
                }
                disableClearable={false}
                sx={{ width: 280 }}
              />
            </SettingsRow>
          )}
        </SettingsSection>

        <SettingsSection
          title="Document handling"
          description="What Pixydocs does with documents when they arrive."
        >
          <SettingsRow
            label="Exclude from AI Auto Assign"
            description="Uploaded documents are never assigned to this project automatically."
            htmlFor="exclude-auto-assign"
          >
            <Switch
              id="exclude-auto-assign"
              checked={project.exclude_from_doc_routing}
              onChange={(_e, checked) => handleExcludeFromDocRouting(checked)}
            />
          </SettingsRow>
          <SettingsRow
            label="Remove duplicate entities"
            description="Drops extra predicted values that are likely duplicates, such as chips found in more than one place on the page."
            htmlFor="remove-duplicate-entities"
          >
            <Switch
              id="remove-duplicate-entities"
              checked={project.remove_duplicate_entities}
              onChange={(_e, checked) => handleRemoveDupEntities(checked)}
            />
          </SettingsRow>
          <SettingsRow
            label="Contains personal information"
            description="Marks documents in this project as holding personally identifiable information (PII)."
            htmlFor="documents-contain-pii"
          >
            <Switch
              id="documents-contain-pii"
              checked={project.documents_contain_pii}
              onChange={(_e, checked) =>
                debouncedUpdateProject({
                  ...project,
                  documents_contain_pii: checked,
                })
              }
            />
          </SettingsRow>
        </SettingsSection>

        <SettingsSection title="Danger zone" tone="danger">
          <SettingsRow
            label="Delete this project"
            description="Deletes the project and every document in it. This can't be undone."
          >
            <Tooltip
              title={isTemplateProject ? 'Cannot delete template projects' : ''}
            >
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteOverlay.open}
                  disabled={isTemplateProject}
                >
                  Delete project
                </Button>
              </span>
            </Tooltip>
          </SettingsRow>
        </SettingsSection>
      </SettingsPage>

      <DeleteProjectDialog
        name={project.name}
        onDelete={onDeleteProject}
        overlay={deleteOverlay}
      />
    </>
  )
}
